// src/components/pages/Cart.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const Cart = () => {
    const {
        cartItems,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
    } = useCart();

    // Separar por tipo
    const machineRequests = cartItems.filter(i => i.itemType === 'machineRequest');
    const machineryItems = cartItems.filter(i => i.itemType === 'machinery');
    const spareParts = cartItems.filter(i => i.itemType === 'sparePart');

    return (
        <div className="container mx-auto px-6 py-24 max-w-4xl">
            {/* Breadcrumb */}
            <nav className="text-sm mb-12">
                <Link to="/" className="text-text-muted hover:text-dark-primary">Inicio</Link>
                <span className="mx-2 text-text-muted">/</span>
                <span className="text-dark-primary font-medium">Carrito</span>
            </nav>

            <h1 className="text-4xl md:text-5xl font-display font-bold text-dark-primary mb-8">
                Carrito de Cotización
            </h1>

            {cartItems.length === 0 ? (
                <div className="text-center py-16 bg-surface-subtle border border-border-subtle">
                    <p className="text-text-muted text-lg">Tu carrito está vacío</p>
                    <Link
                        to="/machinery"
                        className="inline-block mt-6 font-display text-xs uppercase tracking-luxury font-medium bg-dark-primary text-white hover:bg-black px-8 py-3 transition-all duration-300"
                    >
                        Explorar maquinaria
                    </Link>
                </div>
            ) : (
                <>
                    {/* ============================================================
                        1) MAQUINARIA
                        ============================================================ */}
                    {machineryItems.length > 0 && (
                        <section className="mb-10">
                            <h2 className="font-display text-sm uppercase tracking-luxury text-text-muted font-semibold mb-4">
                                Maquinaria
                            </h2>

                            <div className="bg-white border border-border-subtle overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-surface-subtle border-b border-border-subtle">
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Modelo</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Tipo</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Precio</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Cantidad</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Subtotal</th>
                                                <th className="px-6 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {machineryItems.map((item) => (
                                                <tr
                                                    key={item.id}
                                                    className="border-b border-border-subtle hover:bg-surface-subtle"
                                                >
                                                    <td className="px-6 py-4 font-display font-semibold text-dark-primary">
                                                        {item.name}
                                                    </td>
                                                    <td className="px-6 py-4 text-xs text-jcb-yellow uppercase tracking-luxury font-display">
                                                        {item.machineryType || '—'}
                                                    </td>
                                                    <td className="px-6 py-4 text-text-muted">
                                                        {item.price === 0 || item.price == null ? (
                                                            <span className="text-jcb-yellow font-medium">A cotizar</span>
                                                        ) : (
                                                            `$${Number(item.price).toLocaleString('en-US', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })} USD`
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => updateQuantity(item.id, item.quantity - 1, item.contextKey)}
                                                                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                                            >
                                                                -
                                                            </button>
                                                            <span className="w-8 text-center font-display text-dark-primary">
                                                                {item.quantity}
                                                            </span>
                                                            <button
                                                                onClick={() => updateQuantity(item.id, item.quantity + 1, item.contextKey)}
                                                                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 font-display font-medium text-dark-primary">
                                                        {item.price === 0 || item.price == null ? (
                                                            <span className="text-jcb-yellow font-medium">A cotizar</span>
                                                        ) : (
                                                            `$${(Number(item.price) * item.quantity).toLocaleString('en-US', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })} USD`
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => removeFromCart(item.id, item.contextKey)}
                                                            className="text-text-muted hover:text-red-500 transition-colors"
                                                            aria-label={`Eliminar ${item.name}`}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}

                    {/* ============================================================
                        2) REFACCIONES
                        ============================================================ */}
                    {spareParts.length > 0 && (
                        <section className="mb-10">
                            <h2 className="font-display text-sm uppercase tracking-luxury text-text-muted font-semibold mb-4">
                                Refacciones
                            </h2>

                            <div className="bg-white border border-border-subtle overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full border-collapse">
                                        <thead>
                                            <tr className="bg-surface-subtle border-b border-border-subtle">
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Refacción</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Precio</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Cantidad</th>
                                                <th className="px-6 py-3 text-left font-display text-xs uppercase tracking-luxury text-text-muted">Subtotal</th>
                                                <th className="px-6 py-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {spareParts.map((item) => (
                                                <tr
                                                    key={`${item.id}-${item.contextKey}`}
                                                    className="border-b border-border-subtle hover:bg-surface-subtle"
                                                >
                                                    <td className="px-6 py-4 font-display font-medium text-dark-primary">
                                                        {item.name}
                                                    </td>
                                                    <td className="px-6 py-4 text-text-muted">
                                                        {item.price === 0 || item.price == null ? (
                                                            <span className="text-jcb-yellow font-medium">A cotizar</span>
                                                        ) : (
                                                            `$${Number(item.price).toLocaleString('en-US', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })} USD`
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => updateQuantity(item.id, item.quantity - 1, item.contextKey)}
                                                                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                                            >
                                                                -
                                                            </button>
                                                            <span className="w-8 text-center font-display text-dark-primary">
                                                                {item.quantity}
                                                            </span>
                                                            <button
                                                                onClick={() => updateQuantity(item.id, item.quantity + 1, item.contextKey)}
                                                                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-dark-primary transition-colors border border-border-subtle rounded"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 font-display font-medium text-dark-primary">
                                                        {item.price === 0 || item.price == null ? (
                                                            <span className="text-jcb-yellow font-medium">A cotizar</span>
                                                        ) : (
                                                            `$${(Number(item.price) * item.quantity).toLocaleString('en-US', {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            })} USD`
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => removeFromCart(item.id, item.contextKey)}
                                                            className="text-text-muted hover:text-red-500 transition-colors"
                                                            aria-label={`Eliminar ${item.name}`}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}
                    {/* ============================================================
                        3) SOLICITUDES DE REFACCIONES
                        ============================================================ */}
                    {machineRequests.length > 0 && (
                        <section className="mb-10">
                            <h2 className="font-display text-sm uppercase tracking-luxury text-text-muted font-semibold mb-4">
                                Solicitudes de Refacciones
                            </h2>

                            <div className="space-y-4">
                                {machineRequests.map((req) => (
                                    <div key={req.id} className="bg-white border border-border-subtle overflow-hidden">
                                        <div className="bg-dark-primary text-white px-6 py-3 flex items-center justify-between">
                                            <div className="font-display text-xs uppercase tracking-luxury text-jcb-yellow">
                                                Búsqueda por Maquinaria
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeFromCart(req.id, null)}
                                                className="text-white/70 hover:text-red-400 transition-colors"
                                                aria-label="Eliminar solicitud"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-6">
                                            <div>
                                                <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">
                                                    Tipo
                                                </span>
                                                <span className="font-display font-semibold text-dark-primary text-sm">
                                                    {req.machineryType}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">
                                                    Modelo
                                                </span>
                                                <span className="font-display font-semibold text-dark-primary text-sm">
                                                    {req.model}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">
                                                    Año
                                                </span>
                                                <span className="font-display font-semibold text-dark-primary text-sm">
                                                    {req.year}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-display text-[10px] uppercase tracking-luxury text-text-subtle block mb-1">
                                                    Área
                                                </span>
                                                <span className="font-display font-semibold text-dark-primary text-sm">
                                                    {req.area}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Totales generales */}
                    {/* Totales generales */}
                    <div className="mt-8 bg-surface-subtle p-6 border border-border-subtle">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <span className="text-sm text-text-muted">Total de items: </span>
                                <span className="font-display font-medium text-dark-primary">{totalItems}</span>
                            </div>
                            <div>
                                <span className="text-sm text-text-muted">Subtotal refacciones: </span>
                                <span className="font-display font-semibold text-dark-primary text-lg">
                                    {totalPrice > 0 ? (
                                        `$${totalPrice.toLocaleString('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })} USD`
                                    ) : (
                                        <span className="text-jcb-yellow">A cotizar</span>
                                    )}
                                </span>
                            </div>
                        </div>

                        {(machineryItems.length > 0 || machineRequests.length > 0) && (
                            <p className="mt-3 pt-3 border-t border-border-subtle text-xs text-text-muted">
                                La maquinaria y las solicitudes de equipos se cotizan de forma personalizada según disponibilidad.
                            </p>
                        )}
                    </div>

                    <div className="mt-8 flex flex-wrap gap-4">
                        <button
                            onClick={clearCart}
                            className="font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-8 py-3 transition-all"
                        >
                            Vaciar carrito
                        </button>
                        <Link
                            to="/contact"
                            className="font-display text-xs uppercase tracking-luxury font-medium bg-jcb-yellow text-dark-primary hover:bg-yellow-500 px-8 py-3 transition-all"
                        >
                            Solicitar Cotización
                        </Link>
                        <Link
                            to="/machinery"
                            className="font-display text-xs uppercase tracking-luxury font-medium border border-border-medium text-text-muted hover:text-dark-primary hover:border-dark-primary px-8 py-3 transition-all"
                        >
                            Seguir comprando
                        </Link>
                    </div>
                </>
            )}
        </div>
    );
};

export default Cart;